import { EventAggregator } from 'aurelia-event-aggregator';
import {
	autoinject,
	bindable,
	BindingEngine,
	computedFrom,
	customElement,
	PLATFORM,
	useView,
} from 'aurelia-framework';
import { FluentRuleCustomizer } from 'aurelia-validation';

import { Field } from '~/components/field/common/field';
import { Form } from '~/components/form/form';
import { NumberFormatValueConverter } from '~/converters/number-format';
import {
	DECIMAL_DIGITS,
	MAX_INT,
	MAX_LONG,
	MIN_INT,
	MIN_LONG,
	NumberFormat,
} from '~/converters/number-format-constants';
import {
	commonFieldProperties,
	registerComponent,
} from '~/forms/customisation-editor/property-meta';
import { DateTypeInputMaskAliasMap } from '~/inputmasks/input-mask';
import {
	calculateIntegerDigitsFromInputmask,
	calculateNumericAliasFromInputmask,
	convertInputMask,
} from '~/inputmasks/input-mask-converter';
import { DataType, DataTypeEnum } from '~/models/data-type';
import { FieldModel, Modifiable } from '~/models/form-model';
import { Key } from '~/utils/key';
import { NumberScaleUtils } from '~/utils/number-scale-utils';
import { CustomValidationRuleName } from '~/validation/custom-validation-rules';

export const componentTagNameNumber = 'a-number';

registerComponent(componentTagNameNumber, {
	name: 'Number',
	requirePath: PLATFORM.moduleName('components/field/common/number'),
	props: commonFieldProperties,
	isSelectable: true,
});

@autoinject
@customElement('a-number')
@useView(PLATFORM.moduleName('./number.html'))
export class Number extends Field {
	/**
	 * Number of decimal units to be displayed
	 */
	@bindable private scale: number = null;
	/**
	 * Override number type to be a currency (for conversion)
	 */
	@bindable private isCurrency: boolean = false;
	/**
	 * Override number type to be a percantage field (for conversion)
	 */
	@bindable private isPercentage: boolean = false;
	/**
	 * The input mask of the field. This is overridden by fieldModal.inputMask.
	 */
	@bindable private inputMask: string;
	/**
	 * Align the field to the right, this is to make units / currency more readable.
	 * If you're not sure on when to use this or not, consider the stack exchange answer below:
	 * See: https://ux.stackexchange.com/questions/88968/is-it-good-ux-to-right-align-numerical-data-in-a-text-field-when-editing/88985#88985
	 */
	private alignText: 'left' | 'right' = 'left';

	/**
	 * Override default text alignment behaviour.
	 */
	@bindable private overrideAlignText: string = undefined;

	constructor(
		public element: Element,
		protected parentForm: Form,
		readonly eventAggregator: EventAggregator,
		protected bindingEngine: BindingEngine,
		private numberFormatConverter: NumberFormatValueConverter,
	) {
		super(Field.TYPE.NUMBER, element, parentForm, eventAggregator);
		this.inputType = 'text';
		if (this.overrideAlignText !== undefined) {
			this.alignText =
				this.overrideAlignText === 'right' ? 'right' : 'left';
		}
	}

	public attached() {
		super.attached();
		this.applyInputmask();
	}

	protected fieldModelChanged(fieldModel: FieldModel) {
		super.fieldModelChanged(fieldModel);
		if (fieldModel) {
			// input masks for number are dependent upon datatypes like currency/percentage etc
			this.eventSubscriptions.push(
				this.bindingEngine
					.propertyObserver(this.fieldModel, 'dataType')
					.subscribe((newValue, oldValue) => {
						this.onDataTypeChanged(newValue, oldValue);
					}),
			);

			this.setDataTypeAttributes(fieldModel.dataType);
		}
	}

	private setDataTypeAttributes(dataType: DataType) {
		this.isCurrency = this.dataTypeName === 'Currency';
		this.isPercentage = this.dataTypeName === 'Percentage';

		// NOTE(Jake): 2018-10-04 - WEBUI-1054
		// Force float and currency fields to align right if no alignment
		// explicitly given.
		if (this.overrideAlignText !== undefined) {
			this.alignText =
				this.overrideAlignText === 'right' ? 'right' : 'left';
		} else {
			const isFloatOrCurrency =
				this.isCurrency || this.dataTypeName === 'BigDecimal';
			this.alignText = isFloatOrCurrency ? 'right' : 'left';
		}
	}

	protected onDataTypeChanged(newVal: DataType, oldVal: DataType) {
		this.setDataTypeAttributes(this.fieldModel.dataType);
		// input masks are dependent on fieldModel.datatype and so need to update
		this.applyInputmask();
	}

	protected onKeydown(event: KeyboardEvent) {
		// NOTE(Jake): 2018-09-03 - WEBUI-935
		//
		// <input type="number"> accepts, 'e', 'E', and '+' for
		// scientific notation. We want to explictly block these
		// characters from being enterable, but we allow '-' for
		// negative number input.
		//
		if (
			event.key === Key.e ||
			event.key === Key.E ||
			event.key === Key.PLUS
		) {
			return false;
		}
		return true;
	}

	protected onPaste(e: ClipboardEvent) {
		// NOTE(Jake): 2018-09-03 - WEBUI-935
		//
		// <input type="number"> accepts, 'e', 'E', and '+' for
		// scientific notation. We want to block the "paste" operation
		// if the clipboard contains the characters checked below, but
		// we allow '-' for negative number input.
		const clipboardData = e.clipboardData || (window as any).clipboardData;
		const pastedData: string = clipboardData.getData('Text').toUpperCase();
		if (
			pastedData.indexOf('e') > -1 ||
			pastedData.indexOf('E') > -1 ||
			pastedData.indexOf('+') > -1
		) {
			return false;
		}
		return true;
	}

	/**
	 * Override.
	 *
	 * Get calculated inputmask options.
	 * If an input mask string is defined, return options with a corresponding predefined alias for that input mask
	 * along with the string as the mask.
	 * If the data type of this field is set, return the default calculated options for that data type (which includes
	 * an alias).
	 * Else return undefined.
	 */
	@computedFrom('fieldModel', 'fieldModel.inputMask', 'inputMask')
	get inputmaskOptions(): inputmask.InputmaskOptions | undefined {
		let fieldInputMask: string | undefined = this.inputMask;
		if (this.fieldModel) {
			fieldInputMask = this.fieldModel.inputMask;
		}

		let fieldDataType: DataType | undefined = this.fieldDataType;
		if (this.fieldModel) {
			fieldDataType = this.fieldModel.dataType;
		}

		if (fieldInputMask) {
			const alias =
				calculateNumericAliasFromInputmask(fieldInputMask) ||
				DateTypeInputMaskAliasMap[this.dataTypeName];

			// if number field is a currency/percentage, we calculate mask options based on data type
			// and scale and integerDigits based on mask because setting mask property removes decorators
			if (
				fieldDataType &&
				(fieldDataType.name === 'Currency' ||
					fieldDataType.name === 'Percentage')
			) {
				const maskOptions = this.calcInputMaskOptions(
					this.fieldDataType,
					undefined,
				);
				const integerDigits =
					calculateIntegerDigitsFromInputmask(fieldInputMask);
				// integerDigits only need to be calculated for currency/percentage fields with masks
				// because other number fields already have their integers set by their mask and
				// currency/percentage fields without masks have no restriction on their integers
				maskOptions.integerDigits =
					integerDigits > -1 ? integerDigits : undefined;
				return maskOptions;
			}

			return {
				alias,
				mask: convertInputMask(fieldInputMask),
			};
		} else {
			if (!fieldDataType) {
				return undefined;
			}

			// If an input mask string is not provided, provide a default numeric inputmask options object
			if (this.fieldModel) {
				return this.calcInputMaskOptions(
					fieldDataType,
					this.fieldModel.value,
				);
			} else {
				return this.calcInputMaskOptions(fieldDataType, undefined);
			}
		}
	}

	@computedFrom('value', 'fieldModel.value', 'scale', 'dataTypeName')
	get displayValue() {
		let val;
		if (
			this.fieldModel &&
			(this.fieldModel.modifiable === Modifiable.TRUE ||
				this.fieldModel.modifiable === Modifiable.INSERT_ONLY)
		) {
			val = this.fieldModel.value;
		} else {
			val = this.value;
		}
		// NOTE: Jake: 2019-11-06 - WEBUI-2416
		// If a fieldModel is defined then the scale should be taken exclusively
		// from the fieldModel.
		// This change was made mostly to prevent usage in customisations, which is banned.
		// Even for other cases, the better solution is to probably apply the scale via Java in the backend.
		const scale =
			this.fieldModel && this.fieldModel.dataType
				? this.fieldModel.dataType.scale
				: this.scale;
		return this.numberFormatConverter.toView(val, scale, this.dataTypeName);
	}

	protected setFieldValidation(
		field: Field,
		fluentRulesCustomizer: FluentRuleCustomizer<Field, any>,
	): void {
		super.setFieldValidation(field, fluentRulesCustomizer);
		if (!field.fieldModel || !field.fieldModel.dataType) {
			return;
		}
		switch (field.fieldModel.dataType.name) {
			case 'Integer':
				fluentRulesCustomizer.satisfiesRule(
					CustomValidationRuleName.NUMERIC_RANGE,
					MIN_INT,
					MAX_INT,
				);
				break;
			case 'Long':
				fluentRulesCustomizer.satisfiesRule(
					CustomValidationRuleName.NUMERIC_RANGE,
					MIN_LONG,
					MAX_LONG,
				);
				break;
			case 'BigDecimal':
			case 'Currency':
				fluentRulesCustomizer.satisfiesRule(
					CustomValidationRuleName.DECIMAL_DIGITS,
					DECIMAL_DIGITS,
				);
				break;
		}
	}

	private calcInputMaskOptions(
		dataType: DataType,
		value: string | undefined,
	): inputmask.NumericInputmaskOptions {
		const inputmaskOpts: inputmask.NumericInputmaskOptions = {
			autoUnmask: true,
		};
		// alias
		inputmaskOpts.alias = DateTypeInputMaskAliasMap[dataType.name];
		// auto group number integer part
		if (
			inputmaskOpts.alias &&
			!inputmaskOpts.alias.endsWith('WithoutAutoGroup') &&
			NumberFormat.Integer.AutoGroup
		) {
			inputmaskOpts.autoGroup = true;
			inputmaskOpts.groupSeparator = ',';
		}
		// use `alignText` property
		inputmaskOpts.rightAlign = this.alignText === 'right';
		// scale
		const fixedDigits = NumberScaleUtils.getScale(
			dataType.name,
			value,
			dataType.scale,
		);
		if (dataType.scale === undefined || dataType.scale === null) {
			if (fixedDigits < NumberFormat.Decimal.Digits) {
				inputmaskOpts.digits = `${fixedDigits},${NumberFormat.Decimal.Digits}`;
			} else {
				// round value
				inputmaskOpts.digits = `${NumberFormat.Decimal.Digits},${NumberFormat.Decimal.Digits}`;
			}
		} else {
			inputmaskOpts.digits = `${fixedDigits},${fixedDigits}`;
		}

		return inputmaskOpts;
	}

	@computedFrom('fieldModel')
	get dataTypeName(): DataTypeEnum | '' {
		return this.fieldModel?.dataType ? this.fieldModel.dataType.name : '';
	}

	/**
	 * Special handler for changed number values. Fires when record is changed or when end field fires
	 * @override
	 */
	protected valueChanged(newValue, oldValue) {
		// To prevent value change events from firing when "0.0" changes to "0.00", we need to compare
		// actual value of number field rather than if field is readonly because end fields trigger
		// changes to field model values in the same way but not all fields are readonly in this case.
		// We want to prevent this so "Unsaved changes" modals do not appear, even if you modify nothing,
		// but also to prevent other end fields from inadvertantly firing.
		// Only number fields should be affected by this
		if (
			!this.fieldModel ||
			parseFloat(this.fieldModel.value) === parseFloat(newValue)
		) {
			return;
		}
		super.valueChanged(newValue, oldValue);
	}
}
